import React from 'react';
import { useHilChatRenderer } from '../context/HilChatRendererContext';
import { HilChatTextMessage } from './HilChatTextMessage';
import { HilChatFormMessage } from './HilChatFormMessage';
import type { HilChatMessage } from '../types/HilChatMessage';
import type { HilChatWindowSettings, HilChatMessageRenderer } from '../settings/HilChatWindowSettings';

const SHIPPED_DEFAULTS: Record<HilChatMessage['kind'], HilChatMessageRenderer> = {
  text: HilChatTextMessage,
  form: HilChatFormMessage,
};

interface HilChatMessageItemProps {
  message: HilChatMessage;
  settings: HilChatWindowSettings;
  disabled?: boolean;
  onFormValuesChange?: (values: Record<string, unknown>) => void;
  onRetry?: () => void;
}

/**
 * Dispatch precedence, fixed and documented here since two override mechanisms
 * coexist at the same per-message-kind granularity (see ChatWindow-Design.md §7):
 *   1. settings.renderers[kind]      — plain-prop override, app-instance scoped
 *   2. useHilChatRenderer(kind)      — context override, tree-scoped
 *   3. shipped default renderer
 * Both (1) and (2) may be supplied simultaneously for different kinds without conflict.
 */
export function HilChatMessageItem({ message, settings, disabled, onFormValuesChange, onRetry }: HilChatMessageItemProps): React.JSX.Element {
  const contextRenderer = useHilChatRenderer(message.kind);
  const renderer =
    settings.renderers?.[message.kind] ?? contextRenderer ?? SHIPPED_DEFAULTS[message.kind];

  return (
    <>
      {renderer({
        message,
        settings,
        disabled,
        onFormValuesChange,
        onRetry: message.status === 'failed' ? onRetry : undefined,
      })}
    </>
  );
}
